import { Chip, Tooltip } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { TestRunStatusList, type TestSuiteRun } from "../types.ts";
import { formatDurationFromTimes } from "../utils";
import { humanizeText } from "../utils/text.ts";
import { CreatedByColumn } from "./created-by-column.tsx";
import { GitBranchChip } from "./git-branch-chip.tsx";
import { RunStatusChip } from "./run-status-chip.tsx";

export const suiteRunColumns: GridColDef<TestSuiteRun>[] = [
  {
    field: "test_suite_name",
    headerName: "Suite",
    minWidth: 240,
    flex: 1,
    type: "string",
    sortable: false, // Can't sort by joined column easily
  },
  {
    field: "start_time",
    type: "dateTime",
    headerName: "Started At",
    valueGetter: (val: TestSuiteRun["start_time"]) => {
      if (!val) {
        return null;
      }

      return new Date(val);
    },
    minWidth: 200,
    flex: 1,
    sortable: true,
  },
  {
    field: "created_by",
    type: "string",
    headerName: "Started By",
    width: 150,
    renderCell: CreatedByColumn,
    sortable: false,
  },
  {
    field: "elapsed",
    type: "string",
    headerName: "Duration",
    valueGetter: (_, val) => {
      return formatDurationFromTimes(val?.start_time, val?.end_time);
    },
    width: 120,
    sortable: false,
  },
  {
    field: "run_status",
    type: "singleSelect",
    valueOptions: TestRunStatusList,
    headerName: "Status",
    renderCell: (params: GridRenderCellParams<TestSuiteRun>) => {
      const status = params.row.run_status;
      const finalStatus = params.row.final_run_status;

      let label = humanizeText(status);
      let tooltip = "";

      if (finalStatus) {
        label = `${humanizeText(finalStatus)} *`;
        tooltip = `The original status was ${humanizeText(status)}`;
      }

      return (
        <Tooltip title={tooltip}>
          <span>
            <RunStatusChip status={finalStatus ?? status} label={label} />
          </span>
        </Tooltip>
      );
    },
    width: 180,
    sortable: true,
  },
  {
    field: "trigger",
    type: "singleSelect",
    valueOptions: ["manual", "cron", "webhook", "action"],
    headerName: "Trigger",
    renderCell: (params: GridRenderCellParams<TestSuiteRun>) => {
      const getColor = (status: TestSuiteRun["trigger"]) => {
        switch (status) {
          case "manual": {
            return "default";
          }
          case "cron": {
            return "info";
          }
          case "webhook": {
            return "success";
          }
          case "action": {
            return "success";
          }
          case "pw_reporter": {
            return "primary";
          }
        }
      };

      return (
        <Chip
          label={humanizeText(params.value)}
          variant="outlined"
          size="small"
          color={getColor(params.value as TestSuiteRun["trigger"])}
        />
      );
    },
    width: 140,
    sortable: true,
  },
  {
    field: "shard_total",
    headerName: "Shards",
    width: 100,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TestSuiteRun>) => {
      const shardTotal = params.row.shard_total;
      const shardsCompleted = params.row.shards_completed ?? 0;

      if (!shardTotal) {
        return null; // Not a sharded run
      }

      return (
        <Chip
          label={`${shardsCompleted}/${shardTotal}`}
          size="small"
          variant="outlined"
          color={shardsCompleted === shardTotal ? "success" : "warning"}
          sx={{ fontSize: "0.75rem" }}
        />
      );
    },
  },
  {
    field: "git_branch",
    type: "string",
    headerName: "Branch",
    width: 150,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TestSuiteRun>) => {
      return (
        <GitBranchChip
          branch={params.row.git_branch}
          commitSha={params.row.git_commit_sha}
          commitShaShort={params.row.git_commit_sha_short}
          commitMessage={params.row.git_commit_message}
          remoteOrigin={params.row.git_remote_origin}
          isDirty={params.row.git_is_dirty}
          showCommit
        />
      );
    },
  },
];
